import { Injectable } from "@angular/core";
import type MapView from "@arcgis/core/views/MapView";
import type SceneView from "@arcgis/core/views/SceneView";
import type ArcGISMap from "@arcgis/core/Map";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import Color from "@arcgis/core/Color";
import { BaseNotifier } from "../../../../shared/notifiers/base-notifier";
import { Ref } from "../../../../shared/providers/ref";
import { PointWeatherRepositoryProvider } from "../../../../shared/providers/repository-providers";
import { MapProvider } from "../map/map-provider";
import { PopupInteractionProvider } from "../identify/popup-interaction/popup-interaction-provider";
import { WeatherPointState } from "./weather-point-state";

type ArcGisView = MapView | SceneView;

/** Id de la capa de gráficos que dibuja el marcador del clic. */
const WEATHER_MARKER_LAYER_ID = "weather-point-marker";

/**
 * Consulta meteorológica puntual al hacer clic en el mapa.
 *
 * Flujo:
 *  1. Captura la coordenada del clic (`mapPoint`) y su posición en pantalla.
 *  2. Pregunta a Open-Meteo (`IPointWeatherRepository`) el tiempo actual y del
 *     día para ese punto, más la ubicación administrativa que lo contiene.
 *  3. Publica el resultado para que el popup lo dibuje.
 *
 * El popup se cierra al desplazar/hacer zoom (la pantalla ya no corresponde al
 * punto) y con la tecla Escape. Cuando la herramienta de identificación está
 * activa se respeta su comportamiento y no se abre este popup.
 */
@Injectable()
export class WeatherPointNotifier extends BaseNotifier<WeatherPointState> {
  private static readonly INITIAL: WeatherPointState = {
    open: false,
    loading: false,
    error: null,
    longitude: null,
    latitude: null,
    screenX: null,
    screenY: null,
    viewWidth: 0,
    viewHeight: 0,
    weather: null,
  };

  private _clickHandle: { remove(): void } | null = null;
  private _extentHandle: { remove(): void } | null = null;
  private _attachedView: ArcGisView | null = null;

  /** Capa de gráficos que dibuja el marcador del punto consultado. */
  private _markerLayer: GraphicsLayer | null = null;

  /** Token monotónico para descartar respuestas obsoletas en vuelo. */
  private _token = 0;

  constructor(ref: Ref) {
    super(ref, WeatherPointNotifier.INITIAL);
    this.trackDisposable(() => this.detach());
    this.trackDisposable(this.listenEscape());
  }

  override build(): void {
    this.listen(() => {
      const view = this.ref.read(MapProvider).state().view as ArcGisView | null;
      if (view && view !== this._attachedView) this.attach(view);
    });
  }

  override onDispose(): void {
    this.detach();
  }

  /** Cierra el popup (baja el estado a reposo). */
  close(): void {
    if (!this.snapshot().open) return;
    this._token++;
    this.removeMarker();
    this.updateState((s) => ({
      ...s,
      open: false,
      loading: false,
      error: null,
      weather: null,
    }));
  }

  // ── Ciclo de vida de la vista ─────────────────────────────────────

  private attach(view: ArcGisView): void {
    this.detach();
    this._attachedView = view;
    this._clickHandle = view.on("click", (event: any) => {
      void this.onClick(view, event);
    });
    this._extentHandle = view.watch("extent", () => this.close());
  }

  private detach(): void {
    this._clickHandle?.remove();
    this._extentHandle?.remove();
    this._clickHandle = null;
    this._extentHandle = null;
    this._attachedView = null;
    this.removeMarker();
  }

  private listenEscape(): () => void {
    const handler = (event: KeyboardEvent): void => {
      if (event.key === "Escape") this.close();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }

  // ── Consulta ──────────────────────────────────────────────────────

  private async onClick(view: ArcGisView, event: any): Promise<void> {
    // La herramienta de identificación manda cuando está activa.
    if (this.ref.read(PopupInteractionProvider).state().enabled) return;

    const mapPoint = event?.mapPoint;
    if (!mapPoint) return;
    const longitude = mapPoint.longitude;
    const latitude = mapPoint.latitude;
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return;
    if (longitude < -180 || longitude > 180 || latitude < -90 || latitude > 90) {
      return;
    }

    const token = ++this._token;
    this.updateState((s) => ({
      ...s,
      open: true,
      loading: true,
      error: null,
      longitude,
      latitude,
      screenX: typeof event.x === "number" ? event.x : null,
      screenY: typeof event.y === "number" ? event.y : null,
      viewWidth: view.width ?? 0,
      viewHeight: view.height ?? 0,
      weather: null,
    }));

    // El clic puede haber abierto el popup nativo de la capa (auto-open). La
    // app usa su propio popup para el clima, así que lo retiramos: primero de
    // inmediato (menos parpadeo) y de nuevo tras la respuesta, que es cuando el
    // popup nativo ya se ha asentado.
    view.closePopup();

    // Marcador del punto consultado: anillo con punto central para que se vea
    // con claridad dónde se hizo el clic.
    this.placeMarker(view, longitude, latitude);

    const result = await this.ref
      .read(PointWeatherRepositoryProvider)
      .instance.getPoint({ longitude, latitude });
    if (this._disposed || token !== this._token) return;
    view.closePopup();

    this.updateState((s) => ({
      ...s,
      loading: false,
      weather: result.success ? result.data ?? null : null,
      error: result.success
        ? null
        : result.message ?? "No se pudo obtener el clima del punto",
    }));
  }

  // ── Marcador del punto ────────────────────────────────────────────

  /** Dibuja el marcador (anillo + centro) en la coordenada consultada. */
  private placeMarker(view: ArcGisView, longitude: number, latitude: number): void {
    this.removeMarker();
    const map = view.map as ArcGISMap | null | undefined;
    if (!map || !map.layers) return;

    const layer = this.ensureMarkerLayer(map);
    const point = new Point({ longitude, latitude });
    // Anillo exterior: resalta la posición sobre cualquier basemap.
    layer.add(
      new Graphic({
        geometry: point,
        symbol: new SimpleMarkerSymbol({
          style: "circle",
          color: new Color([255, 255, 255, 1]),
          size: 15,
          outline: { color: new Color([20, 40, 60, 1]), width: 2 },
        }),
      }),
    );
    // Punto central: color de la familia temperatura/clima.
    layer.add(
      new Graphic({
        geometry: point,
        symbol: new SimpleMarkerSymbol({
          style: "circle",
          color: new Color([232, 133, 58, 1]),
          size: 5,
        }),
      }),
    );
  }

  /** Borra el marcador actual. */
  private removeMarker(): void {
    this._markerLayer?.removeAll();
  }

  /** Capa de gráficos del marcador, creada una sola vez por mapa. */
  private ensureMarkerLayer(map: ArcGISMap): GraphicsLayer {
    if (this._markerLayer) return this._markerLayer;
    const existing = map.layers.find(
      (l) => (l as GraphicsLayer).id === WEATHER_MARKER_LAYER_ID,
    );
    if (existing instanceof GraphicsLayer) {
      this._markerLayer = existing;
      return existing;
    }
    const layer = new GraphicsLayer({
      id: WEATHER_MARKER_LAYER_ID,
      listMode: "hide",
      visible: true,
    });
    map.add(layer);
    this._markerLayer = layer;
    return layer;
  }
}
